import { Injectable, signal } from '@angular/core';

/**
 * Detects WebGL capabilities and exposes them as Angular signals so components
 * can reactively show/hide UI based on GPU support.
 *
 * Replaces the old Features.js module — all detection logic lives here.
 */
@Injectable({ providedIn: 'root' })
export class FeaturesService {
  readonly interpolationSupported = signal(false);
  readonly splatsSupported = signal(false);
  readonly edlSupported = signal(false);
  readonly precision = signal<'highp' | 'mediump' | 'lowp' | null>(null);

  constructor() {
    const detected = detectWebGLFeatures();
    if (detected) {
      this.interpolationSupported.set(detected.interpolationSupported);
      this.splatsSupported.set(detected.splatsSupported);
      this.edlSupported.set(detected.edlSupported);
      this.precision.set(detected.precision);
    }
    // Populate the module-level cache
    _cachedFeatures = detected;
  }
}

// ─── Detection logic (moved from Features.js) ────────────────────────────────

interface DetectedFeatures {
  interpolationSupported: boolean;
  splatsSupported: boolean;
  edlSupported: boolean;
  precision: 'highp' | 'mediump' | 'lowp';
}

function detectWebGLFeatures(): DetectedFeatures | null {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
  if (!gl || !(gl instanceof WebGLRenderingContext)) {
    return null;
  }

  const vertexHigh = gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.HIGH_FLOAT);
  const vertexMedium = gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.MEDIUM_FLOAT);
  const fragmentHigh = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
  const fragmentMedium = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.MEDIUM_FLOAT);

  const highpAvailable = (vertexHigh?.precision ?? 0) > 0 && (fragmentHigh?.precision ?? 0) > 0;
  const mediumpAvailable =
    (vertexMedium?.precision ?? 0) > 0 && (fragmentMedium?.precision ?? 0) > 0;

  let precision: 'highp' | 'mediump' | 'lowp';
  if (highpAvailable) {
    precision = 'highp';
  } else if (mediumpAvailable) {
    precision = 'mediump';
  } else {
    precision = 'lowp';
  }

  const hasFragDepth = !!gl.getExtension('EXT_frag_depth');
  const hasTextureFloat = !!gl.getExtension('OES_texture_float');
  const maxVarying = gl.getParameter(gl.MAX_VARYING_VECTORS) as number;

  const interpolationSupported = hasFragDepth && maxVarying >= 8;
  const splatsSupported = hasFragDepth && hasTextureFloat && maxVarying >= 8;
  const edlSupported = hasFragDepth && hasTextureFloat && maxVarying >= 8;

  // Clean up the context
  const ext = gl.getExtension('WEBGL_lose_context');
  ext?.loseContext();

  return { interpolationSupported, splatsSupported, edlSupported, precision };
}

// ─── Module-level accessor for non-DI code ─────────────────────────────────────

let _cachedFeatures: DetectedFeatures | null = null;

/**
 * Get detected features for non-DI code.
 * Returns an object compatible with the old Features.js API shape.
 */
export function getFeatures() {
  if (!_cachedFeatures) {
    _cachedFeatures = detectWebGLFeatures();
  }
  return _cachedFeatures
    ? {
        SHADER_INTERPOLATION: { isSupported: () => _cachedFeatures!.interpolationSupported },
        SHADER_SPLATS: { isSupported: () => _cachedFeatures!.splatsSupported },
        SHADER_EDL: { isSupported: () => _cachedFeatures!.edlSupported },
        precision: _cachedFeatures.precision,
      }
    : null;
}
